/**
 * Settings: notifications, 2FA, data export, sign out.
 *
 * The data export is here rather than buried in a support email because it is
 * a GDPR right and a captain should be able to exercise it without asking
 * anyone's permission. It returns this vessel's own record only -- other
 * vessels' data is excluded even where this captain was permitted to see it.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Field, Notice } from '@abg/ui'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { checkBuild, type BuildStatus } from '../lib/build'
import { clearCache } from '../lib/offline'
import type { NotificationSettings } from '../lib/types'
import {
  isIos,
  isStandalone,
  isSubscribed,
  pushSupport,
  subscribe,
  unsubscribe,
  type PushSupport,
} from '../lib/push'

export function SettingsPage() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [support, setSupport] = useState<PushSupport>('unsupported')
  const [subscribed, setSubscribed] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setSupport(pushSupport())
    setSubscribed(await isSubscribed().catch(() => false))
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const togglePush = async () => {
    setNote(null)
    setError(null)
    try {
      if (subscribed) {
        await unsubscribe()
        setNote('Notifications off. Decisions still appear in the app.')
      } else if (!(await subscribe())) {
        setNote('Notifications were not enabled.')
      }
      await refresh()
    } catch {
      setError('Could not change notification settings.')
    }
  }

  const exportData = async () => {
    setError(null)
    try {
      const payload = await api.get<unknown>('/api/export/me')
      // Anchor-download rather than a share sheet: this has to work in an
      // installed PWA on both platforms, and a Blob URL is the one path that
      // does.
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `abg-anchoring-${user?.vessel_name?.replace(/\s+/g, '-').toLowerCase() ?? 'vessel'}.json`
      link.click()
      URL.revokeObjectURL(url)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not build the export.')
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1>Settings</h1>
        <span className="subtitle">
          {user?.name} · {user?.vessel_name ?? 'no vessel assigned'}
        </span>
      </header>

      {error && <Notice tone="alert">{error}</Notice>}
      {note && <Notice>{note}</Notice>}

      <section className="card stack">
        <strong>Notifications</strong>
        {support === 'ios-needs-install' ? (
          <Notice tone="warn">
            On iPhone and iPad, notifications work only when the app has been added to your home
            screen. Tap Share → Add to Home Screen in Safari, then reopen from there.
          </Notice>
        ) : support === 'denied' ? (
          <Notice tone="warn">
            Blocked in your browser settings. Change it there, then come back.
          </Notice>
        ) : support === 'unsupported' ? (
          <Notice>This browser cannot do push notifications.</Notice>
        ) : (
          <>
            <label className="switch">
              <input type="checkbox" checked={subscribed} onChange={togglePush} />
              <span>Notify me about decisions and window reminders</span>
            </label>
            <p className="caption">
              Your own setting, not the vessel's — everyone with an account on{' '}
              {user?.vessel_name ?? 'this vessel'} chooses for themselves. Turning it off
              stops notifications reaching this device; every decision still appears in the
              app, and the rest of the crew are unaffected.
            </p>
          </>
        )}
        {isIos() && isStandalone() && (
          <p className="caption">Installed on your home screen — push will work here.</p>
        )}
      </section>

      <NotificationChoices />

      <section className="card stack">
        <strong>Two-factor authentication</strong>
        <p className="small">
          {user?.totp_enabled
            ? 'On. You are asked for a code from your authenticator app when you sign in.'
            : 'Optional for captains. Adds an authenticator code to sign-in.'}
        </p>
        <TotpControls enabled={Boolean(user?.totp_enabled)} />
      </section>

      <section className="card stack">
        <strong>Your data</strong>
        <p className="small">
          Download everything we hold for your vessel: applications, anchor reports, group
          memberships and the history of who did what. Other vessels' data is not included.
        </p>
        <button type="button" className="btn btn-quiet" onClick={exportData}>
          Download my vessel's data
        </button>
      </section>

      <section className="card stack">
        <strong>Offline data</strong>
        <p className="small">
          The app keeps the last chart, anchorage list and applications it saw, so it opens with
          no signal. Clearing this does not delete anything from our records.
        </p>
        <button
          type="button"
          className="btn btn-quiet"
          onClick={async () => {
            await clearCache()
            setNote('Saved data cleared.')
          }}
        >
          Clear saved data
        </button>
      </section>

      <button
        type="button"
        className="btn btn-danger btn-lg btn-block"
        onClick={async () => {
          await signOut()
          navigate('/', { replace: true })
        }}
      >
        Sign out
      </button>

      <BuildStamp />

      <p className="caption" style={{ textAlign: 'center' }}>
        Superyacht Services
      </p>
    </div>
  )
}

/**
 * Which build this device is running, and whether it is the served one.
 *
 * Not vanity. A service worker can pin an app to a build that no longer exists
 * on the server, and from the outside that app looks and behaves perfectly --
 * offline included, because everything it needs is in its own caches. Twice in
 * one day a real device reported a bug that had already been fixed, and the
 * only way to tell was somebody on the far end fetching the bundle and
 * grepping it. See items 32 and 33.
 *
 * Three states, kept distinct, for the same reason the speed-zone popup keeps
 * three (decision 53): up to date, not the served build, and **could not
 * ask**. Folding the last into either of the others would tell a captain
 * something nobody checked.
 *
 * The reload button appears only when there is something to reload into. It is
 * safe here in a way an automatic reload is not (see main.tsx): a captain in
 * Settings is not halfway through an application form.
 */
function BuildStamp() {
  const [status, setStatus] = useState<BuildStatus | null>(null)
  const [checking, setChecking] = useState(false)

  const check = useCallback(async () => {
    setChecking(true)
    try {
      setStatus(await checkBuild())
    } finally {
      setChecking(false)
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  return (
    <section className="card stack">
      <strong>App version</strong>
      <p className="small">
        Quote this if you report a problem — it says which build of the app this device is
        actually running, which is not always the newest one.
      </p>
      <ul className="usage-list">
        <li>
          <span>This device</span>
          <span className="mono">{status?.build ?? '…'}</span>
        </li>
        {status?.cacheVersion && (
          <li>
            <span>Offline store</span>
            <span className="mono">{status.cacheVersion}</span>
          </li>
        )}
      </ul>

      {status?.server === 'current' && <span className="caption">Up to date.</span>}
      {status?.server === 'unknown' && (
        <span className="caption">
          No connection, so we could not check whether this is the newest build.
        </span>
      )}
      {status?.server === 'not-current' && (
        <>
          <Notice tone="warn">
            A newer build is available ({status.serverBuild}). Reload to pick it up — anything
            you have queued offline is kept.
          </Notice>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </>
      )}

      <button type="button" className="btn btn-quiet" onClick={check} disabled={checking}>
        {checking ? 'Checking…' : 'Check again'}
      </button>
    </section>
  )
}

/**
 * What to be notified about.
 *
 * Rendered from a catalogue the server supplies, so a notification kind added
 * to the fan-out cannot be missing from this screen. A captain is only offered
 * kinds that make sense for them: nothing here for "Posted", "Anchored" or
 * "Anchor aweigh", because those are things they did themselves.
 *
 * Turning one off stops the push. It does **not** stop the record — every
 * decision still appears in the app, because the in-app list is the system of
 * record and push is the courtesy layer.
 */
function NotificationChoices() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    void api
      .get<NotificationSettings>('/api/push/settings')
      .then(setSettings)
      .catch(() => setFailed(true))
  }, [])

  if (failed || !settings) return null

  const toggle = async (kind: string, enabled: boolean) => {
    setBusy(true)
    try {
      setSettings(
        await api.post<NotificationSettings>('/api/push/settings', { kind, enabled }),
      )
    } catch {
      /* leave the switch as the server last reported it */
    } finally {
      setBusy(false)
    }
  }

  const groups = [
    { key: 'status', title: 'What happens to my applications' },
    { key: 'reminder', title: 'Reminders' },
  ]

  return (
    <section className="card stack">
      <strong>Notify me about</strong>
      {groups.map((group) => {
        const kinds = settings.kinds.filter((k) => k.category === group.key)
        if (kinds.length === 0) return null
        return (
          <div key={group.key} className="stack-sm">
            <div className="eyebrow">{group.title}</div>
            {kinds.map((kind) => (
              <div key={kind.key}>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={Boolean(settings.mine[kind.key])}
                    disabled={busy}
                    onChange={(e) => void toggle(kind.key, e.target.checked)}
                  />
                  <span>{kind.label_en}</span>
                </label>
                {kind.help_en && <p className="caption">{kind.help_en}</p>}
              </div>
            ))}
          </div>
        )
      })}
      <p className="caption">
        Turning one off stops the notification reaching your devices. It still appears in the app
        — nothing is hidden from you, and the rest of the crew are unaffected.
      </p>
    </section>
  )
}


function TotpControls({ enabled }: { enabled: boolean }) {
  const { reload } = useAuth()
  const [setup, setSetup] = useState<{ secret: string; provisioning_uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<string | null>(null)

  if (enabled) {
    return <p className="caption">To turn this off, contact us — it protects your applications.</p>
  }

  if (!setup) {
    return (
      <button
        type="button"
        className="btn btn-quiet"
        onClick={async () => {
          setSetup(await api.post('/api/auth/totp/setup'))
        }}
      >
        Set up two-factor
      </button>
    )
  }

  return (
    <div className="stack-sm">
      <p className="small">
        Add this key to your authenticator app, then enter the six-digit code it shows.
      </p>
      <div className="mono" style={{ wordBreak: 'break-all' }}>
        {setup.secret}
      </div>
      <Field label="Code from your app">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={8}
        />
      </Field>
      <button
        type="button"
        className="btn"
        onClick={async () => {
          try {
            await api.post('/api/auth/totp/confirm', { code })
            await reload()
            setStatus('Two-factor is on.')
            setSetup(null)
          } catch (caught) {
            setStatus(caught instanceof ApiError ? caught.message : 'That code was not accepted.')
          }
        }}
      >
        Confirm
      </button>
      {status && <span className="caption">{status}</span>}
    </div>
  )
}
